// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { format } from 'date-fns';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  TableBody,
  Chip,
  Pagination,
  useTheme,
  TableContainer,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Search, Refresh } from '@mui/icons-material';
import { IconLock, IconEye, IconPlus } from '@tabler/icons-react';
import { ApiTicket, TicketStatus } from '@/types/apps/ticket';
import { TicketContext } from '@/context/TicketContext';
import CreateTicketModal from './CreateTicketModal';
import { useAuth } from '@/context/AuthContext';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

const TicketListing = () => {
  const {
    apiTickets,
    filter,
    loading,
    page,
    totalPages,
    total,
    fetchTickets,
    setPage,
    ticketIdSearch,
    setTicketIdSearch,
  }: any = useContext(TicketContext);

  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [closingTicketId, setClosingTicketId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Handle close ticket
  const handleCloseTicket = async (ticketId: string) => {
    try {
      setClosingTicketId(ticketId);
      await apiService.closeTicket(ticketId);
      
      // Оновити список тікетів після закриття
      let status: TicketStatus | undefined = undefined;
      if (filter === 'Open') status = TicketStatus.OPEN;
      else if (filter === 'Closed') status = TicketStatus.CLOSED;
      await fetchTickets(page, status);
    } catch (error: any) {
      console.error('Error closing ticket:', error);
      alert(error.message || t('tickets.listing.failedToClose'));
    } finally {
      setClosingTicketId(null);
    }
  };

  // Handle page change
  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    let status: TicketStatus | undefined = undefined;
    if (filter === 'Open') status = TicketStatus.OPEN;
    else if (filter === 'Closed') status = TicketStatus.CLOSED;
    fetchTickets(value, status);
  };

  // Handle filter change
  useEffect(() => {
    let status: TicketStatus | undefined = undefined;
    if (filter === 'Open') status = TicketStatus.OPEN;
    else if (filter === 'Closed') status = TicketStatus.CLOSED;
    fetchTickets(1, status);
  }, [filter]);

  // Check for openCreate query parameter
  useEffect(() => {
    if (searchParams.get('openCreate') === 'true') {
      setOpenCreateModal(true);
      // Remove query parameter from URL
      searchParams.delete('openCreate');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ticketBadge = (status: string) => {
    return status === TicketStatus.OPEN
      ? theme.palette.success.light
      : status === TicketStatus.CLOSED
        ? theme.palette.error.light
        : theme.palette.primary.light;
  };

  return (
    <Box mt={4}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => {
            let status: TicketStatus | undefined = undefined;
            if (filter === 'Open') status = TicketStatus.OPEN;
            else if (filter === 'Closed') status = TicketStatus.CLOSED;
            fetchTickets(page, status, ticketIdSearch || undefined);
          }}
          disabled={loading}
        >
          {t('tickets.listing.refresh')}
        </Button>
        {user?.userType === 'customer' && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<IconPlus size="20" />}
            onClick={() => setOpenCreateModal(true)}
          >
            {t('tickets.listing.newTicket')}
          </Button>
        )}
      </Box>

      <CreateTicketModal
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onSuccess={() => {
          // Оновити список тікетів після успішного створення
          let status: TicketStatus | undefined = undefined;
          if (filter === 'Open') status = TicketStatus.OPEN;
          else if (filter === 'Closed') status = TicketStatus.CLOSED;
          fetchTickets(1, status);
          setPage(1);
          
          // Показати алерт про успішне створення
          setSnackbar({
            open: true,
            message: t('tickets.listing.ticketCreatedSuccess'),
            severity: 'success',
          });
        }}
        onError={(error) => {
          // Показати алерт про помилку
          setSnackbar({
            open: true,
            message: error,
            severity: 'error',
          });
        }}
      />
      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder={t('tickets.listing.searchPlaceholder')}
          value={ticketIdSearch}
          onChange={e => {
            setTicketIdSearch(e.target.value);
            setPage(1);
            let status: TicketStatus | undefined = undefined;
            if (filter === 'Open') status = TicketStatus.OPEN;
            else if (filter === 'Closed') status = TicketStatus.CLOSED;
            fetchTickets(1, status, e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, maxWidth: { xs: '100%', sm: '400px' } }}
        />
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="h6">{t('tickets.listing.id')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('tickets.listing.subject')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('tickets.listing.supportType')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('tickets.listing.status')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('tickets.listing.createdDate')}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="h6"></Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiTickets.length > 0 ? (
                  apiTickets.map((ticket: ApiTicket) => (
                    <TableRow key={ticket.id} hover>
                      <TableCell>
                        <Typography variant="subtitle1">{ticket.shortId}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="h6" fontWeight={600} noWrap>
                            {ticket.subject}
                          </Typography>
                          <Typography
                            color="textSecondary"
                            noWrap
                            sx={{ maxWidth: '300px' }}
                            variant="subtitle2"
                            fontWeight={400}
                          >
                            {ticket.message}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle1" textTransform="capitalize">
                          {ticket.supportType}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          sx={{
                            backgroundColor: ticketBadge(ticket.status),
                          }}
                          size="small"
                          label={t(`tickets.status.${ticket.status}`)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle1">
                          {format(new Date(ticket.createdAt), 'dd-MM-yyyy HH:mm')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          {ticket.status === TicketStatus.OPEN && (
                            <Tooltip title={t('tickets.listing.closeTicket')}>
                              <IconButton 
                                color="error"
                                onClick={() => handleCloseTicket(ticket.id)}
                                disabled={closingTicketId === ticket.id}
                              >
                                {closingTicketId === ticket.id ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <IconLock size="20" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={t('tickets.listing.viewTicket')}>
                            <IconButton 
                              color="primary"
                              onClick={() => navigate(`/tickets/${ticket.id}`, {
                                state: {
                                  subject: ticket.subject,
                                  status: ticket.status,
                                  supportType: ticket.supportType,
                                  customer: ticket.customer,
                                },
                              })}
                            >
                              <IconEye size="20" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="h6" color="textSecondary" py={4}>
                        {t('tickets.listing.noTicketsFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 1 && (
            <Box my={3} display="flex" justifyContent={'center'}>
              <Pagination
                count={totalPages}
                page={page}
                color="primary"
                onChange={handlePageChange}
              />
            </Box>
          )}
          <Box display="flex" justifyContent={'center'} mb={2}>
            <Typography variant="body2" color="textSecondary">
              {t('tickets.listing.showing', { count: apiTickets.length, total })}
            </Typography>
          </Box>
        </>
      )}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          variant="filled"
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TicketListing;
